import { create } from "zustand";
import type { ToastProps } from "@/ui";

type ToastId = number;
type ToastItem = Omit<ToastProps, "onClose"> & { id: ToastId };
type ToastInput = Omit<ToastProps, "onClose"> & { id?: ToastId };
type ToastState = { toasts: ToastItem[] };

let nextId = 1;

// A minimal toast queue backing the @/ui Toast component — react-hot-toast
// (used everywhere else in the app) has no @/ui-native equivalent, so this
// gives the email-builder flow (see lib/toast/toast.js) an imperative
// toast.success/error/loading API that renders through @/ui instead.
const useToastStore = create<ToastState>(() => ({
  toasts: [],
}));

// Call sites routinely do `dismiss(loadingId); success(...)` back to back
// (e.g. after an await, swapping a "Deleting…" toast for "Deleted") — batch
// same-tick mutations into a single commit, and have the `push` reuse the
// id that was `dismiss`-ed earlier in the SAME batch (see `push` below) so
// React reconciles it as the same element rather than unmounting the
// loading toast and mounting a brand new success toast in its place, which
// otherwise reads as a flicker.
let pending: ToastItem[] | null = null;
let flushScheduled = false;
let lastDismissedInBatch: ToastId | null = null;

const queueUpdate = (updater: (toasts: ToastItem[]) => ToastItem[]) => {
  if (pending === null) {
    pending = useToastStore.getState().toasts;
  }
  pending = updater(pending);

  if (!flushScheduled) {
    flushScheduled = true;
    queueMicrotask(() => {
      useToastStore.setState({ toasts: pending ?? [] });
      pending = null;
      flushScheduled = false;
      lastDismissedInBatch = null;
    });
  }
};

export const push = (toast: ToastInput) => {
  let id = toast.id;
  if (id === undefined) {
    id = lastDismissedInBatch ?? nextId++;
  }
  lastDismissedInBatch = null;

  // Toasts are intentionally latest-wins: a new notification replaces any
  // existing one instead of building an obstructive stack of stale messages.
  queueUpdate(() => [{ ...toast, id }]);

  // Expire from the store itself on a real timer, not just via the
  // mounted Toast component's own effect — a toast fired just before
  // navigating away (e.g. "Deleting draft…" right before redirecting to
  // Float Details) would otherwise never actually leave the store, and
  // could resurface if something remounts the toast host later with the
  // duration timer restarted from zero.
  if (toast.duration) {
    // Allow the mounted Toast to finish its standard 150ms exit animation
    // before this navigation-safe fallback removes it from the store.
    setTimeout(() => dismiss(id), toast.duration + 150);
  }

  return id;
};

export const dismiss = (id?: ToastId) => {
  if (id !== undefined) {
    lastDismissedInBatch = id;
  }
  queueUpdate((toasts) => (id === undefined ? [] : toasts.filter((t) => t.id !== id)));
};

export default useToastStore;
